import React, { useCallback, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { withProps, withHandlers, compose } from 'recompose'
import { inject, observer } from 'mobx-react'
import { toJS } from 'mobx'
import AddressInput from '../AddressInput'
import Dropdown from '../../../Shared/Dropdown'
import PriceInput from '../../../Shared/Form/PriceInput'
import SearchBarFooterActions from '../../Shared/SearchBarFooterActions'
import SelectPricePeriod from '../../Shared/SelectPricePeriod/SelectPricePeriod'
import BedroomsBaths from '../BedroomsBaths'
import AdvancedFilter from '../AdvancedFilter'
import { priceLabelWithCutOff } from '../../../../lib/utils/money'
import { enhanceWithMap } from '../../../Shared/Form/TownSelect'
import { FilterUrl } from '../../../Svg'

const enhance = compose(
  inject('store'),
  withProps(({ store }) => ({
    uiStateStore: store.uiStateStore,
    listingTypeStore: store.listingTypeStore,
    valueStateStore: store.valueStateStore
  })),
  enhanceWithMap,
  withHandlers(() => {
    const dropdownElements = {}

    const createCancelHandler = (name) => () => () => {
      dropdownElements[name]?.stateUpdaters?.deactivate?.()
    }

    return {
      registerDropdown: () => (name, el) => {
        if (el) {
          dropdownElements[name] = el
        }
      },
      cancelPriceDropdown: createCancelHandler('price'),
      cancelPricePeriodDropdown: createCancelHandler('pricePeriod'),
      cancelBedsBathsDropdown: createCancelHandler('bedsBaths'),
      cancelAdvancedFilterDropdown: createCancelHandler('advancedFilter'),
      onChangeTown: ({ valueStateStore }) => ({ value }) => {
        valueStateStore.setInputValue('townNames', value)
        valueStateStore.getSearchInfoDebounced()
      },
      onImmediateSubmit: ({ valueStateStore }) => () => {
        valueStateStore.fetchDataForListings()
        valueStateStore.getSearchInfoDebounced()
      },
      onChangePrice: ({ valueStateStore }) => (name, value) => {
        valueStateStore.setInputValue(name, value)
        valueStateStore.getSearchInfoDebounced()
      },
      onChangePricePeriod: ({ valueStateStore }) => (values) => {
        valueStateStore.setInputValue('pricePeriods', values)
        valueStateStore.getSearchInfoDebounced()
      },
      onChangeYear: ({ valueStateStore }) => (year) => {
        valueStateStore.setInputValue('year', year)
        valueStateStore.getSearchInfoDebounced()
      },
      onRadioButtonChange: ({ valueStateStore }) => (name) => (value) => {
        valueStateStore.setInputValue(name, value)
        valueStateStore.getSearchInfoDebounced()
      }
    }
  }),
  observer
)

const SearchBarMobile = ({
  valueStateStore,
  listingTypeStore,
  uiStateStore,
  registerDropdown,
  cancelPriceDropdown,
  cancelPricePeriodDropdown,
  cancelBedsBathsDropdown,
  cancelAdvancedFilterDropdown,
  onChangeTown,
  onImmediateSubmit,
  onChangePrice,
  onChangePricePeriod,
  onChangeYear,
  onRadioButtonChange,
  openMapView
}) => {
  const [isListingTypeActive, setIsListingTypeActive] = useState(false)
  const [isPriceActive, setIsPriceActive] = useState(false)
  const [isPricePeriodActive, setIsPricePeriodActive] = useState(false)
  const [isBedsBathsActive, setIsBedsBathsActive] = useState(false)
  const [isAdvancedFilterActive, setIsAdvancedFilterActive] = useState(false)

  const listingTypeDropdownRef = useRef(null)
  const priceDropdownRef = useRef(null)
  const pricePeriodDropdownRef = useRef(null)
  const bedsBathsDropdownRef = useRef(null)
  const advancedFilterDropdownRef = useRef(null)

  const handleSubmitTownChanges = useCallback(() => {
    valueStateStore.clearMapBounds()
    valueStateStore.removePolygon()
    onImmediateSubmit()
  }, [onImmediateSubmit, valueStateStore])

  const createApplyHandler = useCallback((cancelHandler) => () => {
    onImmediateSubmit()
    cancelHandler()
  }, [onImmediateSubmit])

  const handleApplyPrice = createApplyHandler(cancelPriceDropdown)
  const handleApplyPricePeriod = createApplyHandler(cancelPricePeriodDropdown)
  const handleApplyBedsBaths = createApplyHandler(cancelBedsBathsDropdown)
  const handleAdvancedFilterChange = createApplyHandler(cancelAdvancedFilterDropdown)

  const createDropdownToggleHandler = (isActive, setIsActive, dropdownRef, dropdownName) => (e) => {
    e?.preventDefault()
    e?.stopPropagation()

    const ref = dropdownRef.current
    const newActive = !isActive

    if (newActive) {
      const dropdowns = [
        { name: 'listingType', ref: listingTypeDropdownRef, setState: setIsListingTypeActive },
        { name: 'price', ref: priceDropdownRef, setState: setIsPriceActive },
        { name: 'pricePeriod', ref: pricePeriodDropdownRef, setState: setIsPricePeriodActive },
        { name: 'bedsBaths', ref: bedsBathsDropdownRef, setState: setIsBedsBathsActive },
        { name: 'advancedFilter', ref: advancedFilterDropdownRef, setState: setIsAdvancedFilterActive }
      ]

      dropdowns.forEach(({ name, ref: dropdownRef, setState }) => {
        if (name !== dropdownName && dropdownRef.current?.stateUpdaters) {
          dropdownRef.current.stateUpdaters.deactivate()
          setState(false)
        }
      })
    }

    if (ref?.stateUpdaters) {
      if (newActive) {
        ref.stateUpdaters.setIsActive(true)
      } else {
        ref.stateUpdaters.deactivate()
      }
    }
    setIsActive(newActive)
  }

  return (
    <div className='SearchBar SearchBar--mobile'>
      <div className='SearchBar-mobileAddressInput'>
        <AddressInput
          value={valueStateStore.values.address || ''}
          onChange={(e) => valueStateStore.setInputValue('address', e.target.value)}
          placeholder='Address, neighbourhood, city, ZIP'
          options={toJS(valueStateStore.treeTownOptions)}
          optionsById={valueStateStore.optionsById}
          selectedOptionsWithParents={valueStateStore.selectedOptionsWithParents}
          selectedOptionsGroup={toJS(valueStateStore.selectedOptionsGroup)}
          onApplyResults={handleSubmitTownChanges}
          countResults={valueStateStore.countResults}
          onChangeTown={onChangeTown}
          openMapView={openMapView}
        />
      </div>

      <div className='SearchBar-mobileFiltersContainer'>
        <div className='SearchBar-mobileFiltersRow'>
          <div className={`SearchBar-input SearchBar-input--listingType ${isListingTypeActive ? 'is-active' : ''}`}>
            <button
              type="button"
              className="SearchBar-mobileFilterButton SearchBar-mobileFilterButton--listingType"
              onClick={createDropdownToggleHandler(isListingTypeActive, setIsListingTypeActive, listingTypeDropdownRef, 'listingType')}
              aria-expanded={isListingTypeActive}
            >
              {listingTypeStore.current === 'rentals' ? 'Rent' : 'Sale'}
            </button>
            <Dropdown
              id='listing-type-mobile'
              title=""
              className='Dropdown--listingType Dropdown--searchBar Dropdown--mobile'
              onActiveChanged={setIsListingTypeActive}
              ref={(el) => {
                listingTypeDropdownRef.current = el
                registerDropdown('listingType', el)
              }}
            >
            <div className='ListingTypeSelector-options'>
              <div className='ListingTypeSelector-option'>
                <input
                  type='radio'
                  id='listing-type-rentals-mobile'
                  name='listing-type-mobile'
                  value='rentals'
                  checked={listingTypeStore.current === 'rentals'}
                  onChange={() => {
                    valueStateStore.saveSearchScope('rentals')
                    listingTypeStore.setCurrent('rentals')
                    valueStateStore.removePolygon()
                    onImmediateSubmit()
                  }}
                  className='ListingTypeSelector-radio'
                />
                <label htmlFor='listing-type-rentals-mobile' className='ListingTypeSelector-label'>
                  For Rent
                </label>
              </div>

              <div className='ListingTypeSelector-option'>
                <input
                  type='radio'
                  id='listing-type-sales-mobile'
                  name='listing-type-mobile'
                  value='sales'
                  checked={listingTypeStore.current === 'sales'}
                  onChange={() => {
                    valueStateStore.saveSearchScope('sales')
                    listingTypeStore.setCurrent('sales')
                    valueStateStore.removePolygon()
                    onImmediateSubmit()
                  }}
                  className='ListingTypeSelector-radio'
                />
                <label htmlFor='listing-type-sales-mobile' className='ListingTypeSelector-label'>
                  For Sale
                </label>
              </div>
            </div>
            </Dropdown>
          </div>

          <div className={`SearchBar-input SearchBar-input--price ${isPriceActive ? 'is-active' : ''}`}>
            <button
              type="button"
              className="SearchBar-mobileFilterButton SearchBar-mobileFilterButton--price"
              onClick={createDropdownToggleHandler(isPriceActive, setIsPriceActive, priceDropdownRef, 'price')}
              aria-expanded={isPriceActive}
            >
              {valueStateStore.priceTitle(uiStateStore.priceExtremum.cutOff)}
            </button>
            <Dropdown
              id='price-mobile'
              className='Dropdown--searchBar'
              onActiveChanged={setIsPriceActive}
              ref={(el) => {
                priceDropdownRef.current = el
                registerDropdown('price', el)
              }}
              title=""
            >
                <div className='Dropdown-container'>
                  <PriceInput
                    priceExtremum={uiStateStore.priceExtremum}
                    values={valueStateStore.priceValues}
                    onChange={onChangePrice}
                    onReset={valueStateStore.resetInputs}
                    handleTemplate={priceLabelWithCutOff}
                  />
                </div>
                <SearchBarFooterActions
                  cancelHandler={cancelPriceDropdown}
                  clearHandler={() => {
                    valueStateStore.resetInputs(['priceFrom', 'priceTo'])
                    valueStateStore.getSearchInfoDebounced()
                  }}
                  applyResultsHandler={handleApplyPrice}
                  countResults={valueStateStore.countResults}
                />
              </Dropdown>
          </div>

          {listingTypeStore.current === 'rentals' &&
          <div className={`SearchBar-input SearchBar-input--pricePeriod ${isPricePeriodActive ? 'is-active' : ''}`}>
            <button
              type="button"
              className="SearchBar-mobileFilterButton SearchBar-mobileFilterButton--pricePeriod"
              onClick={createDropdownToggleHandler(isPricePeriodActive, setIsPricePeriodActive, pricePeriodDropdownRef, 'pricePeriod')}
              aria-expanded={isPricePeriodActive}
            >
              Price Period
            </button>
            <SelectPricePeriod
              id='price-period-mobile'
              pricePeriodYears={uiStateStore.pricePeriodYears}
              selectedYear={valueStateStore.values.year}
              pricePeriods={uiStateStore.pricePeriods}
              selectedPricePeriod={valueStateStore.values.pricePeriods || []}
              onChangePricePeriod={onChangePricePeriod}
              onChangeYear={onChangeYear}
              onResetInputToInitialValue={valueStateStore.resetInputToInitialValue}
              onActiveChanged={setIsPricePeriodActive}
              registerDropdown={(name, el) => {
                pricePeriodDropdownRef.current = el
                registerDropdown(name, el)
              }}
              DropdownComponent={Dropdown}
              footer={
                <SearchBarFooterActions
                  countResults={valueStateStore.countResults}
                  cancelHandler={cancelPricePeriodDropdown}
                  clearHandler={() => {
                    valueStateStore.resetInputs(['pricePeriods', 'year'])
                    valueStateStore.getSearchInfoDebounced()
                  }}
                  applyResultsHandler={handleApplyPricePeriod}
                />
              }
            />
          </div>
          }

          {(listingTypeStore.current === 'rentals' || listingTypeStore.current === 'sales') &&
          !valueStateStore.values.unitTypes?.includes('land') &&
          <div className={`SearchBar-input SearchBar-input--bedsBaths ${isBedsBathsActive ? 'is-active' : ''}`}>
            <button
              type="button"
              className="SearchBar-mobileFilterButton SearchBar-mobileFilterButton--bedsBaths"
              onClick={createDropdownToggleHandler(isBedsBathsActive, setIsBedsBathsActive, bedsBathsDropdownRef, 'bedsBaths')}
              aria-expanded={isBedsBathsActive}
            >
              {valueStateStore.bedsBathsTitle}
            </button>
            <Dropdown
              id='bedrooms-mobile'
              name='bedrooms'
              className='Dropdown--searchBar'
              title=""
              onActiveChanged={setIsBedsBathsActive}
              ref={(el) => {
                bedsBathsDropdownRef.current = el
                registerDropdown('bedsBaths', el)
              }}
            >
                <div className='Dropdown-container Dropdown-container--stacked'>
                  <div className='Dropdown-itemColumn'>
                    <BedroomsBaths
                      name='beds'
                      options={uiStateStore.bedsBathsOptions}
                      onChange={onRadioButtonChange('beds')}
                      value={valueStateStore.values.beds}
                      legend='Bedrooms'
                    />
                  </div>
                  <div className='Dropdown-itemColumn'>
                    <BedroomsBaths
                      name='baths'
                      options={uiStateStore.bedsBathsOptions}
                      onChange={onRadioButtonChange('baths')}
                      value={valueStateStore.values.baths}
                      legend='Bathrooms'
                    />
                  </div>
                </div>
                <SearchBarFooterActions
                  cancelHandler={cancelBedsBathsDropdown}
                  clearHandler={() => {
                    valueStateStore.resetInputs(['beds', 'baths'])
                    valueStateStore.getSearchInfoDebounced()
                  }}
                  applyResultsHandler={handleApplyBedsBaths}
                  countResults={valueStateStore.countResults}
                />
              </Dropdown>
          </div>
          }

          <div className={`SearchBar-input SearchBar-input--advancedFilter ${isAdvancedFilterActive ? 'is-active' : ''}`}>
            <button
              type="button"
              className="SearchBar-mobileFilterButton SearchBar-mobileFilterButton--advancedFilter"
              onClick={(e) => {
                e?.preventDefault()
                e?.stopPropagation()
                advancedFilterDropdownRef.current?.setPreventClose?.(500)
                createDropdownToggleHandler(isAdvancedFilterActive, setIsAdvancedFilterActive, advancedFilterDropdownRef, 'advancedFilter')(e)
              }}
              aria-expanded={isAdvancedFilterActive}
            >
              <img src={FilterUrl} alt="More Filters" />
            </button>
            <AdvancedFilter
              submit={handleAdvancedFilterChange}
              reset={() => valueStateStore.handleResetFilters()}
              onActiveChanged={setIsAdvancedFilterActive}
              registerDropdown={(name, el) => {
                advancedFilterDropdownRef.current = el
                registerDropdown(name, el)
              }}
              cancelDropdown={cancelAdvancedFilterDropdown}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

SearchBarMobile.propTypes = {
  listingTypeStore: PropTypes.object.isRequired,
  onChangePrice: PropTypes.func.isRequired,
  onChangePricePeriod: PropTypes.func.isRequired,
  onChangeTown: PropTypes.func.isRequired,
  onChangeYear: PropTypes.func.isRequired,
  onImmediateSubmit: PropTypes.func.isRequired,
  onRadioButtonChange: PropTypes.func.isRequired,
  openMapView: PropTypes.func.isRequired,
  uiStateStore: PropTypes.object.isRequired,
  valueStateStore: PropTypes.object.isRequired,
  registerDropdown: PropTypes.func.isRequired,
  cancelPriceDropdown: PropTypes.func.isRequired,
  cancelPricePeriodDropdown: PropTypes.func.isRequired,
  cancelBedsBathsDropdown: PropTypes.func.isRequired,
  cancelAdvancedFilterDropdown: PropTypes.func.isRequired
}

export default enhance(SearchBarMobile)
